import { parseAPIErrorResponse, parseStringtoNull } from "utils/utils";
import { useAuth } from "stores/auth/auth-context";
import { useToast } from "components/atoms/toast/toast-context";
import { IApiError } from "services/api/api.types";
import {
  UserType,
  CountryCode,
  Salutation,
  LoginClassType
} from "types/common/common.types";
import jwt_decode from "jwt-decode";

import { registerStudent } from "services/api/profiles/profileService";
import { ParentFormSchema, IUseLoginParent } from "../use-profile-form.types";
import { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { object, string, mixed, bool, InferType } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { phoneRegExp } from "constants/constants";
import { constants } from "constants/constants";
import { useBoolean } from "hooks/use-boolean/use-boolean";
import { useGetCurrentPlatform } from "hooks/use-media-query/use-get-currrent-platform";
import { getSourceInfoEvent } from "utils/mixpanel/mixpanel-utils";
import * as Sentry from "@sentry/nextjs";

const FormSchema = object().shape({
  userType: mixed<UserType>().oneOf(Object.values(UserType)).required(),
  fullName: string().min(2, "Ada yang salah. Coba cek lagi, ya.").required(),
  studentName: string()
    .min(2, "Coba cek lagi nama lengkap anak Anda, ya.")
    .required(),
  studentNumber: string()
    .required("Mohon lengkapi nomor HP kamu")
    .matches(phoneRegExp, "Masukin nomor HP yang aktif, ya.")
    .min(8, "Masukin minimal 8 angka, ya.")
    .max(13, "Hmm... nomornya kepanjangan, nih."),
  classType: mixed<LoginClassType>()
    .oneOf(Object.values(LoginClassType))
    .required("Pilih jenjang sekolah anak Anda."),
  grade: string().required("Pilih kelas anak Anda saat ini."),
  email: string()
    .lowercase()
    .email("Hmm... alamat email ini sepertinya salah.")
    .required(),
  salutation: mixed<Salutation>().oneOf(Object.values(Salutation)).required(),
  isParent: bool().required()
});

const useLoginParentForm = (
  userType: UserType
): IUseLoginParent<ParentFormSchema> => {
  const { addToast } = useToast();
  const { user, signOutUser } = useAuth();
  const { userData, setUserData } = user;
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useBoolean();
  const {
    control,
    handleSubmit,
    formState: { isValid },
    getValues,
    watch
  } = useForm<InferType<typeof FormSchema>>({
    defaultValues: {
      userType: userType,
      salutation: Salutation.Bpk
    },
    mode: "onChange",
    delayError: constants.DELAY_ERROR,
    resolver: yupResolver(FormSchema)
  });

  const currentPlatform = useGetCurrentPlatform();

  const onSubmit: SubmitHandler<ParentFormSchema> = async ({
    email,
    fullName,
    classType,
    grade,
    salutation,
    studentName,
    studentNumber
  }) => {
    try {
      if (userData.register_token) {
        setIsLoading.on();
        const res = await registerStudent({
          payload: {
            name: studentName,
            class_type: classType,
            grade: grade.split("_")[0],
            stream: parseStringtoNull(grade.split("_")[1]),
            email: email.toLowerCase(),
            phone_number: studentNumber,
            country_code: CountryCode.Indonesia,
            notification_consent: user.isWhatsappConsentGiven,
            parent: {
              salutation,
              name: fullName,
              phone_number: decodeJwtTokenFromRegisterToken() || "",
              country_code: CountryCode.Indonesia
            }
          },
          token: userData.register_token,
          platform: getSourceInfoEvent(currentPlatform)
        });

        const {
          message,
          data: { parent_user, student_user }
        } = res;

        setUserData({
          ...userData,
          parent_user: {
            ...userData.parent_user,
            ...parent_user,
            students: [student_user]
          },
          primary_user: student_user,
          register_token: null
        });

        if (message === "OK") {
          setIsSuccessModalOpen(true);
          setIsLoading.off();
        }
      }
    } catch (err) {
      Sentry.captureException(err);
      const error = parseAPIErrorResponse(err);
      const { message } = error as IApiError;
      addToast({
        heading: message,
        variant: "error"
      });
      setIsLoading.off();
    }
  };

  useEffect(() => {
    watch();
  });

  useEffect(() => {
    const userType = getValues().userType;
    user.setUserData({ ...user.userData, userType });
  }, [getValues().userType]);

  const decodeJwtTokenFromRegisterToken = () => {
    try {
      if (user && userData.register_token) {
        const number: { phoneNumber: string } = jwt_decode(
          userData.register_token
        );
        return number.phoneNumber;
      }
    } catch (err) {
      Sentry.captureException(err);
      signOutUser();
    }
  };

  return {
    control,
    getValues,
    handleSubmit,
    isValid,
    onSubmit,
    isSuccessModalOpen,
    setIsSuccessModalOpen,
    isLoading
  };
};

export { useLoginParentForm };
